import { Injectable } from '@angular/core';
import { Host } from '@app/host/host.service';
import { DefaultRouteRedirectGuard } from '@app/guards/default-route-redirect.guard';
import { SidebarLayout } from '@app/sidebar-layout/sidebar-layout.service';
import { ShellAdminPanel } from '@app/shell-admin-panel/shell-admin-panel.service';
import { ShellPartners } from '@app/shell-partners/shell-partners.service';
import { AppRouteHelper } from '@shared/helpers/app-route.helper';
import { WebComponentWrapper, WebComponentWrapperOptions } from '@angular-architects/module-federation-tools';
import { environment } from '@environment';
import { FEATURE_FLAGS } from '@app/infrastructure/feature-flag-manager/feature-flag-list';
import { Shell } from '@app/shell/shell.service';
import { Permissions } from '@app/infrastructure/permission/permission.model';
import { NewSidebarGuard } from '@app/guards/new-sidebar.guard';
import { FeatureFlagGuard } from '@app/infrastructure/feature-flag-manager/feature-flag.guard';
import { AzureOrgGuard } from '@app/guards/azure-org.guard';
import { InsightsOrgGuard } from '@app/guards/insights-org.guard';
import { NewUserProfileGuard } from '@app/guards/new-user-profile.guard';
import { ComponentsOverviewGuard } from '@modules/components-overview/components-overview.guard';
import { BillingApproveComponent } from '@app/components/billing-approve/billing-approve.component';
import { TeamsComponent } from '@app/components/teams-integration/teams.component';
import { SlackComponent } from '@app/components/slack-integration/slack.component';
import { SsoLoginComponent } from '@modules/auth/components/login/sso-login.component';
import { ForbiddenComponent } from '@app/components/forbidden/forbidden.component';
import { ErrorScreenComponent } from '@app/components/error/error-screen/error-screen.component';
import { P404Component } from '@app/components/error/404.component';

@Injectable({
	providedIn: 'root',
})
export class RouterService {
	getAppRoutes() {
		return [
			Host.childRoutes([
				{
					path: '',
					pathMatch: 'full',
					children: [],
					canActivate: [DefaultRouteRedirectGuard],
				},
				SidebarLayout.childRoutes([
					ShellAdminPanel.childRoutes([
						{
							path: '',
							loadChildren: () => import('../modules/admin-panel/admin-panel.module').then((m) => m.AdminPanelModule),
						},
					]),
					// @TODO: ShellPartners.childRoutes will be removed once we fully move to react
					ShellPartners.childRoutes([
						{
							path: AppRouteHelper.partnersPortalOrganizations,
							loadChildren: () =>
								import('../modules/partner-portal/partner-organizations/partner-organizations.module').then(
									(m) => m.PartnerOrganizationsModule,
								),
						},
						{
							path: AppRouteHelper.partnersPortalAccounts,
							loadChildren: () =>
								import('../modules/partner-portal/partner-accounts/partner-accounts.module').then(
									(m) => m.PartnerAccountsModule,
								),
						},
						{
							path: AppRouteHelper.partnersPortalBillings,
							loadChildren: () =>
								import(
									'../modules/partner-portal/features/partner-billing-history/partner-billing-history.module'
								).then((m) => m.PartnerBillingHistoryModule),
						},
						{
							path: AppRouteHelper.partnersPortalReport,
							loadChildren: () =>
								import('../modules/partner-portal/components/partner-report/partner-report.module').then(
									(m) => m.PartnerReportModule,
								),
						},
						{ path: '', redirectTo: AppRouteHelper.partnersPortalOrganizations, pathMatch: 'full' },
					]),
					{
						path: AppRouteHelper.partnersPortal,
						component: WebComponentWrapper,
						data: {
							type: 'module',
							remoteEntry: environment.partnerPortalUrl ? `${environment.partnerPortalUrl}/assets/remoteEntry.js` : '',
							exposedModule: './web-components',
							elementName: 'partner-portal',
							featureFlag: FEATURE_FLAGS.ENABLE_NEW_PARTNER_PORTAL,
						} as WebComponentWrapperOptions,
						children: [
							{ path: '', redirectTo: AppRouteHelper.partnersPortalOrganizations, pathMatch: 'full' },
							{
								path: '**',
								component: WebComponentWrapper,
								data: {
									type: 'module',
									remoteEntry: environment.partnerPortalUrl
										? `${environment.partnerPortalUrl}/assets/remoteEntry.js`
										: '',
									exposedModule: './web-components',
									elementName: 'partner-portal',
									featureFlag: FEATURE_FLAGS.ENABLE_NEW_PARTNER_PORTAL,
								} as WebComponentWrapperOptions,
							},
						],
					},
					Shell.childRoutes([
						{
							path: AppRouteHelper.dashboard,
							data: {
								permissionKey: Permissions.DashboardView,
								activateWhenFeatureFlagDisabled: true,
								alternativePath: AppRouteHelper.defaultRoute,
							},
							canActivate: [NewSidebarGuard],
							loadChildren: () =>
								import('../modules/commitment-manager/modules/dashboard/dashboard.module').then(
									({ DashboardModule }) => DashboardModule,
								),
						},
						{
							path: AppRouteHelper.home,
							component: WebComponentWrapper,
							data: {
								type: 'module',
								remoteEntry: environment.insightsUrl ? `${environment.insightsUrl}/assets/remoteEntry.js` : '',
								exposedModule: './web-components',
								elementName: 'zesty-insights',
								alternativePath: AppRouteHelper.defaultRoute,
							} as WebComponentWrapperOptions,
							canActivate: [NewSidebarGuard],
						},
						{
							path: AppRouteHelper.recommendations,
							component: WebComponentWrapper,
							data: {
								type: 'module',
								remoteEntry: environment.insightsUrl ? `${environment.insightsUrl}/assets/remoteEntry.js` : '',
								exposedModule: './web-components',
								elementName: 'zesty-insights',
								featureFlag: FEATURE_FLAGS.ENABLE_OPTIMIZATION_HUB,
							} as WebComponentWrapperOptions,
							canActivate: [FeatureFlagGuard],
						},
						{
							path: AppRouteHelper.azureVm,
							component: WebComponentWrapper,
							data: {
								type: 'module',
								remoteEntry: environment.azureVmUrl ? `${environment.azureVmUrl}/assets/remoteEntry.js` : '',
								exposedModule: './web-components',
								elementName: 'azure-vm',
							} as WebComponentWrapperOptions,
							canActivate: [AzureOrgGuard],
						},
						{
							path: AppRouteHelper.s3,
							component: WebComponentWrapper,
							data: {
								type: 'module',
								remoteEntry: environment.insightsUrl ? `${environment.insightsUrl}/assets/remoteEntry.js` : '',
								exposedModule: './web-components',
								elementName: 'zesty-insights',
								featureFlag: FEATURE_FLAGS.ENABLE_OPTIMIZATION_HUB,
							} as WebComponentWrapperOptions,
							canActivate: [InsightsOrgGuard],
						},
						/**
						 * 'New' commitmentManagerV2 with new Compute Explorer v2.
						 * Visible only when 'enable-cm-v2-page' FF is enabled.
						 */
						{
							path: AppRouteHelper.commitmentManagerV2,
							canActivate: [FeatureFlagGuard],
							data: {
								featureFlag: FEATURE_FLAGS.ENABLE_CM_V2_PAGE,
								alternativePath: AppRouteHelper.commitmentManager,
							},
							loadChildren: () =>
								import('../modules/commitment-manager-v2/commitment-manager-v2.module').then(
									(m) => m.CommitmentManagerV2Module,
								),
						},
						{
							path: AppRouteHelper.rdsExplorer,
							component: WebComponentWrapper,
							data: {
								type: 'module',
								remoteEntry: environment.commitmentManagerUrl
									? `${environment.commitmentManagerUrl}/assets/remoteEntry.js`
									: '',
								exposedModule: './web-components',
								elementName: 'commitment-manager',
							} as WebComponentWrapperOptions,
						},
						{
							path: AppRouteHelper.kompass,
							component: WebComponentWrapper,
							data: {
								type: 'module',
								remoteEntry: environment.kompassUrl ? `${environment.kompassUrl}/assets/remoteEntry.js` : '',
								exposedModule: './web-components',
								elementName: 'zesty-kompass',
							} as WebComponentWrapperOptions,
							children: [
								{
									path: '**',
									component: WebComponentWrapper,
									data: {
										type: 'module',
										remoteEntry: environment.kompassUrl ? `${environment.kompassUrl}/assets/remoteEntry.js` : '',
										exposedModule: './web-components',
										elementName: 'zesty-kompass',
									} as WebComponentWrapperOptions,
								},
							],
						},
						{
							path: AppRouteHelper.audit,
							component: WebComponentWrapper,
							data: {
								type: 'module',
								remoteEntry: environment.organizationSettingsUrl
									? `${environment.organizationSettingsUrl}/assets/remoteEntry.js`
									: '',
								exposedModule: './web-components',
								elementName: 'organization-settings',
							} as WebComponentWrapperOptions,
						},
						{
							path: AppRouteHelper.riManager,
							data: { permissionKey: Permissions.RiManagerView },
							loadChildren: () =>
								import('../modules/ri-manager/ri-manager.module').then(({ RiManagerModule }) => RiManagerModule),
						},
						{
							path: AppRouteHelper.listings,
							component: WebComponentWrapper,
							data: {
								type: 'module',
								remoteEntry: environment.ec2ListingsUrl ? `${environment.ec2ListingsUrl}/assets/remoteEntry.js` : '',
								exposedModule: './web-components',
								elementName: 'ri-manager',
								permissionKey: Permissions.ListingsView,
							} as WebComponentWrapperOptions,
						},
						{
							path: AppRouteHelper.rfVisualization,
							data: { permissionKey: Permissions.RfVisualization },
							loadChildren: () =>
								import('@modules/rf-visualization/rf-visualization.module').then(
									({ RfVisualizationModule }) => RfVisualizationModule,
								),
						},
						{
							path: AppRouteHelper.dynamicContainers,
							loadChildren: () =>
								import('@modules/dynamic-containers/dynamic-containers.module').then(
									({ DynamicContainersModule }) => DynamicContainersModule,
								),
							/**
							 * dynamic containers are tight with optimization-hub release
							 */
							data: { featureFlag: FEATURE_FLAGS.ENABLE_OPTIMIZATION_HUB },
							canActivate: [FeatureFlagGuard],
						},
						{
							path: AppRouteHelper.accessTokens,
							loadChildren: () =>
								import('@modules/personal-access-tokens-table/personal-access-tokens-list.module').then(
									({ PersonalAccessTokensListModule }) => PersonalAccessTokensListModule,
								),
							/**
							 * @Todo: remove with ZES-13448
							 */
							data: { featureFlag: FEATURE_FLAGS.ENABLE_CONTAINERS_POD_RECOMMENDATION },
							canActivate: [FeatureFlagGuard],
						},
						{
							path: AppRouteHelper.notificationManagement,
							data: { permissionKey: Permissions.NotificationManagementView },
							loadChildren: () =>
								import('../modules/notification-management/notification-management.module').then(
									(m) => m.NotificationManagementModule,
								),
						},
						/**
						 * 'Old' computeExplorer v1 - visible only for old sidebar
						 */
						{
							path: AppRouteHelper.computeExplorer,
							data: {
								activateWhenFeatureFlagDisabled: true,
								alternativePath: AppRouteHelper.commitmentManager + '/' + AppRouteHelper.computeExplorer,
							},
							canActivate: [NewSidebarGuard],
							loadChildren: () =>
								import('../modules/commitment-manager/modules/compute-explorer/compute-explorer.module').then(
									(m) => m.ComputeExplorerModule,
								),
						},
						/**
						 * 'Old' commitmentManager v1.
						 * No direct links but available when 'enable-cm-v2-page' FF is disabled.
						 * It shows tabs with dashboard & 'Old' computeExplorer v1
						 */
						{
							path: AppRouteHelper.commitmentManager,
							data: {
								permissionKey: Permissions.EC2View,
							},
							canActivate: [NewSidebarGuard],
							loadChildren: () =>
								import('../modules/commitment-manager/commitment-manager.module').then(
									(m) => m.CommitmentManagerModule,
								),
						},
						{
							path: AppRouteHelper.myOrganization,
							data: { permissionKey: Permissions.MyOrganizationView },
							loadChildren: () =>
								import('../modules/my-organization/my-organization.module').then((m) => m.MyOrganizationModule),
						},
						{
							path: AppRouteHelper.unusedResources,
							data: { permissionKey: Permissions.UnusedResourcesView },
							loadChildren: () =>
								import('../modules/unused-resources/unused-resources.module').then((m) => m.UnusedResourcesModule),
							canActivate: [InsightsOrgGuard],
						},
						{
							path: AppRouteHelper.anomalyDetection,
							component: WebComponentWrapper,
							data: {
								type: 'module',
								remoteEntry: environment.insightsUrl ? `${environment.insightsUrl}/assets/remoteEntry.js` : '',
								exposedModule: './web-components',
								elementName: 'zesty-insights',
								featureFlag: FEATURE_FLAGS.ENABLE_OPTIMIZATION_HUB,
							} as WebComponentWrapperOptions,
							canActivate: [InsightsOrgGuard],
						},
						{
							path: AppRouteHelper.settings,
							canActivate: [NewUserProfileGuard],
							loadChildren: () => import('../modules/profile/profile.module').then((m) => m.ProfileModule),
						},
						{
							path: AppRouteHelper.userProfile,
							loadChildren: () => import('../modules/profile/profile.module').then((m) => m.ProfileModule),
						},
						{
							path: AppRouteHelper.disk,
							data: { permissionKey: Permissions.ZestyDiskView },
							loadChildren: () => import('../modules/ebs/ebs.module').then((m) => m.EbsModule),
						},
						{
							path: AppRouteHelper.componentsOverview,
							canLoad: [ComponentsOverviewGuard],
							loadChildren: () =>
								import('../modules/components-overview/components-overview.module').then(
									(m) => m.ComponentsOverviewModule,
								),
						},
					]),
				]),
				{ path: 'billing/approve/:id/:date', component: BillingApproveComponent },
				{ path: 'teams/integration-guide', component: TeamsComponent },
				{ path: 'slack/:request', component: SlackComponent },
				{
					path: AppRouteHelper.onboarding,
					loadChildren: () => import('../modules/onboarding/onboarding.module').then((m) => m.OnboardingModule),
				},
				{
					path: AppRouteHelper.onboardAccount,
					loadChildren: () =>
						import('../modules/onboarding/onboard-account/onboard-account.module').then(
							({ OnboardAccountModule }) => OnboardAccountModule,
						),
					canActivate: [FeatureFlagGuard],
					data: {
						featureFlag: FEATURE_FLAGS.ENABLE_NEW_ONBOARDING,
						alternativePath: AppRouteHelper.onboarding,
					},
				},
			]),
			{
				path: '',
				loadChildren: () => import('../modules/auth/auth.module').then((m) => m.AuthModule),
			},
			{
				path: `home/login`,
				redirectTo: `login`,
				pathMatch: 'full',
			},
			{ path: AppRouteHelper.ssoSignIn, component: SsoLoginComponent },
			{ path: AppRouteHelper.forbidden, component: ForbiddenComponent },
			{ path: AppRouteHelper.error, component: ErrorScreenComponent },
			{ path: '**', component: P404Component },
		];
	}
}
